import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
  getDocs
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Loader from './Loader';
import './Dashboard.css';

// Reverse geocoding to get address/landmark from coordinates
async function getAddressFromCoords(latitude, longitude) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'TrackingApp/1.0'
        }
      }
    );
    const data = await response.json();

    if (data && data.address) {
      const addr = data.address;
      // Build a readable address from available parts
      const parts = [];

      // Add specific landmark/building if available
      if (addr.building || addr.amenity || addr.shop || addr.office) {
        parts.push(addr.building || addr.amenity || addr.shop || addr.office);
      }

      // Add road/street
      if (addr.road) {
        parts.push(addr.road);
      }

      // Add neighborhood/suburb/locality
      if (addr.neighbourhood || addr.suburb || addr.locality) {
        parts.push(addr.neighbourhood || addr.suburb || addr.locality);
      }

      // Add city/town
      if (addr.city || addr.town || addr.village) {
        parts.push(addr.city || addr.town || addr.village);
      }

      return parts.length > 0 ? parts.join(', ') : data.display_name?.split(',').slice(0, 3).join(',') || 'Unknown location';
    }
    return 'Unknown location';
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return 'Location unavailable';
  }
}

// Get location with high accuracy
function getLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        const accuracy = Math.round(position.coords.accuracy);

        // Get address/landmark name
        const address = await getAddressFromCoords(latitude, longitude);

        resolve({
          latitude,
          longitude,
          accuracy,
          address,
          timestamp: new Date().toISOString()
        });
      },
      (error) => {
        reject(new Error('Unable to retrieve location: ' + error.message));
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      }
    );
  });
}

function Dashboard() {
  const { currentUser } = useAuth();

  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showEarlyClockOutModal, setShowEarlyClockOutModal] = useState(false);
  const [earlyClockOutReason, setEarlyClockOutReason] = useState('');
  const [isClockingIn, setIsClockingIn] = useState(false);
  const [isClockingOut, setIsClockingOut] = useState(false);
  const [elapsedTime, setElapsedTime] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate total worked time for a record
  const calculateTotalWorkedMs = useCallback((record, endTime = null) => {
    if (!record?.clockIn) return 0;

    const clockInTime = record.clockIn.toDate ? record.clockIn.toDate() : new Date(record.clockIn);
    const clockOutTime = record.clockOut
      ? (record.clockOut.toDate ? record.clockOut.toDate() : new Date(record.clockOut))
      : (endTime || new Date());

    let totalMs = clockOutTime - clockInTime;

    if (record.breaks?.length > 0) {
      record.breaks.forEach((brk) => {
        if (brk.breakIn) {
          const breakIn = brk.breakIn.toDate ? brk.breakIn.toDate() : new Date(brk.breakIn);
          const breakOut = brk.breakOut
            ? (brk.breakOut.toDate ? brk.breakOut.toDate() : new Date(brk.breakOut))
            : (endTime || new Date());
          totalMs -= (breakOut - breakIn);
        }
      });
    }

    return Math.max(0, totalMs);
  }, []);

  // Calculate elapsed time from clock-in
  useEffect(() => {
    if (!todayRecord?.clockIn) {
      setElapsedTime({ hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const calculateElapsed = () => {
      const totalMs = calculateTotalWorkedMs(todayRecord, todayRecord.clockOut ? null : new Date());

      const totalSeconds = Math.floor(totalMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setElapsedTime({ hours, minutes, seconds });
    };

    calculateElapsed();

    if (!todayRecord.clockOut) {
      const timer = setInterval(calculateElapsed, 1000);
      return () => clearInterval(timer);
    }
  }, [todayRecord, currentTime, calculateTotalWorkedMs]);

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Fetch attendance records
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'attendance'),
      where('userId', '==', currentUser.uid),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = [];
      snapshot.forEach((docSnap) => {
        records.push({ id: docSnap.id, ...docSnap.data() });
      });

      const today = getTodayString();
      const todayRec = records.find((r) => r.date === today);
      setTodayRecord(todayRec || null);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching records:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const handleClockIn = async () => {
    if (isClockingIn || todayRecord) return;

    setIsClockingIn(true);

    try {
      // Get user location
      const location = await getLocation();

      const today = getTodayString();
      const checkQuery = query(
        collection(db, 'attendance'),
        where('userId', '==', currentUser.uid),
        where('date', '==', today)
      );
      const existingDocs = await getDocs(checkQuery);

      if (!existingDocs.empty) {
        const existingRecord = existingDocs.docs[0];
        setTodayRecord({ id: existingRecord.id, ...existingRecord.data() });
        setIsClockingIn(false);
        return;
      }

      await addDoc(collection(db, 'attendance'), {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        date: today,
        clockIn: Timestamp.now(),
        clockInLocation: location,
        clockOut: null,
        clockOutLocation: null,
        breaks: [],
        earlyClockOutReason: null
      });
      toast.success('Successfully clocked in!');
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error('Location access is required to clock in. Please allow location permission and try again.');
    } finally {
      setIsClockingIn(false);
    }
  };

  const calculateWorkedHours = () => {
    if (!todayRecord?.clockIn) return 0;

    const clockInTime = todayRecord.clockIn.toDate ? todayRecord.clockIn.toDate() : new Date(todayRecord.clockIn);
    const now = new Date();
    let totalMs = now - clockInTime;

    let totalBreakMs = 0;
    if (todayRecord.breaks?.length > 0) {
      todayRecord.breaks.forEach((brk) => {
        const breakIn = brk.breakIn?.toDate ? brk.breakIn.toDate() : new Date(brk.breakIn);
        const breakOut = brk.breakOut ? (brk.breakOut.toDate ? brk.breakOut.toDate() : new Date(brk.breakOut)) : now;
        totalBreakMs += breakOut - breakIn;
      });
    }

    const maxBreakMs = 60 * 60 * 1000;
    const effectiveBreakMs = Math.min(totalBreakMs, maxBreakMs);
    const workedMs = totalMs - effectiveBreakMs;
    return workedMs / (1000 * 60 * 60);
  };

  const handleClockOutClick = () => {
    const workedHours = calculateWorkedHours();
    if (workedHours < 8) {
      setShowEarlyClockOutModal(true);
    } else {
      performClockOut();
    }
  };

  const performClockOut = async (reason = null) => {
    if (!todayRecord) return;
    setIsClockingOut(true);
    try {
      // Get user location
      const location = await getLocation();

      const updateData = {
        clockOut: Timestamp.now(),
        clockOutLocation: location
      };
      if (reason) updateData.earlyClockOutReason = reason;
      await updateDoc(doc(db, 'attendance', todayRecord.id), updateData);
      setShowEarlyClockOutModal(false);
      setEarlyClockOutReason('');
      toast.success('Successfully clocked out!');
    } catch (error) {
      console.error('Error clocking out:', error);
      toast.error('Location access is required to clock out. Please allow location permission and try again.');
    } finally {
      setIsClockingOut(false);
    }
  };

  const handleEarlyClockOutSubmit = () => {
    if (!earlyClockOutReason.trim()) {
      toast.error('Please provide a reason for early clock out');
      return;
    }
    performClockOut(earlyClockOutReason.trim());
  };

  const handleBreakIn = async () => {
    if (!todayRecord) return;
    try {
      const newBreaks = [...(todayRecord.breaks || []), { breakIn: Timestamp.now(), breakOut: null }];
      await updateDoc(doc(db, 'attendance', todayRecord.id), { breaks: newBreaks });
      toast.success('Break started successfully!');
    } catch (error) {
      console.error('Error starting break:', error);
      toast.error('Failed to start break. Please try again.');
    }
  };

  const handleBreakOut = async () => {
    if (!todayRecord || !todayRecord.breaks?.length) return;
    try {
      const newBreaks = [...todayRecord.breaks];
      newBreaks[newBreaks.length - 1] = {
        ...newBreaks[newBreaks.length - 1],
        breakOut: Timestamp.now()
      };
      await updateDoc(doc(db, 'attendance', todayRecord.id), { breaks: newBreaks });
      toast.success('Break ended successfully!');
    } catch (error) {
      console.error('Error ending break:', error);
      toast.error('Failed to end break. Please try again.');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatElapsedTime = () => {
    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(elapsedTime.hours)} : ${pad(elapsedTime.minutes)} : ${pad(elapsedTime.seconds)}`;
  };

  const isOnBreak = () => {
    if (!todayRecord?.breaks?.length) return false;
    const lastBreak = todayRecord.breaks[todayRecord.breaks.length - 1];
    return lastBreak.breakIn && !lastBreak.breakOut;
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getStatus = () => {
    if (!todayRecord) return { text: 'Out', color: 'red' };
    if (todayRecord.clockOut) return { text: 'Completed', color: 'blue' };
    if (isOnBreak()) return { text: 'On Break', color: 'orange' };
    return { text: 'Working', color: 'green' };
  };

  const isClockedIn = todayRecord?.clockIn && !todayRecord?.clockOut;
  const onBreak = isOnBreak();
  const status = getStatus();

  if (loading) {
    return <Loader message="Loading" />;
  }

  return (
    <div className="dashboard">
      <div className="main-content">
        {/* User Card - Like ZOHO */}
        <div className="user-card">
          <div className="user-avatar">
            {(currentUser.displayName || currentUser.email)[0].toUpperCase()}
          </div>
          <div className="user-name">
            {currentUser.displayName || currentUser.email.split('@')[0]}
          </div>
          <div className={`user-status status-${status.color}`}>
            {status.text}
          </div>

          {/* Timer Display */}
          <div className="timer-display">
            {formatElapsedTime()}
          </div>

          {/* Action Button */}
          <div className="user-card-actions">
            {!todayRecord && (
              <button
                className="btn-checkin"
                onClick={handleClockIn}
                disabled={isClockingIn}
              >
                {isClockingIn ? 'Please wait...' : 'Check-in'}
              </button>
            )}

            {isClockedIn && !onBreak && (
              <button
                className="btn-checkout"
                onClick={handleClockOutClick}
                disabled={isClockingOut}
              >
                {isClockingOut ? 'Checking out...' : 'Check-out'}
              </button>
            )}

            {isClockedIn && onBreak && (
              <button className="btn-breakout" onClick={handleBreakOut}>
                End Break
              </button>
            )}

            {todayRecord?.clockOut && (
              <div className="completed-text">Day Completed</div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="right-section">
          {/* Greeting */}
          <div className="greeting-card">
            <div className="greeting-icon">
              {currentTime.getHours() >= 17 ? '🌙' : currentTime.getHours() >= 12 ? '☀️' : '🌅'}
            </div>
            <div className="greeting-content">
              <h2>{getGreeting()}, {currentUser.displayName || 'User'}!</h2>
              <p>Have a productive day!</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Arrival Time</div>
              <div className="stat-value green">
                {todayRecord ? formatTime(todayRecord.clockIn) : '--:--'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Left Time</div>
              <div className="stat-value red">
                {todayRecord?.clockOut ? formatTime(todayRecord.clockOut) : '--:--'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Working Hours</div>
              <div className="stat-value blue">
                {elapsedTime.hours}<span className="stat-unit">h</span> {elapsedTime.minutes}<span className="stat-unit">m</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Breaks</div>
              <div className="stat-value orange">
                {todayRecord?.breaks?.length || 0}
              </div>
            </div>
          </div>

          {/* Break Button */}
          {isClockedIn && !onBreak && (
            <button className="btn-break" onClick={handleBreakIn}>
              Take a Break
            </button>
          )}

          {/* Today's Timeline */}
          {todayRecord && (
            <div className="timeline-card">
              <h3>Today's Activity</h3>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot green"></div>
                  <div className="timeline-content">
                    <span className="timeline-time">{formatTime(todayRecord.clockIn)}</span>
                    <span className="timeline-label">Checked In</span>
                  </div>
                </div>

                {todayRecord.breaks?.map((brk, idx) => (
                  <React.Fragment key={idx}>
                    <div className="timeline-item">
                      <div className="timeline-dot orange"></div>
                      <div className="timeline-content">
                        <span className="timeline-time">{formatTime(brk.breakIn)}</span>
                        <span className="timeline-label">Break Started</span>
                      </div>
                    </div>
                    {brk.breakOut && (
                      <div className="timeline-item">
                        <div className="timeline-dot blue"></div>
                        <div className="timeline-content">
                          <span className="timeline-time">{formatTime(brk.breakOut)}</span>
                          <span className="timeline-label">Break Ended</span>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {todayRecord.clockOut && (
                  <div className="timeline-item">
                    <div className="timeline-dot red"></div>
                    <div className="timeline-content">
                      <span className="timeline-time">{formatTime(todayRecord.clockOut)}</span>
                      <span className="timeline-label">Checked Out</span>
                      {todayRecord.earlyClockOutReason && (
                        <span className="timeline-reason">Reason: {todayRecord.earlyClockOutReason}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Early Clock Out Modal */}
      {showEarlyClockOutModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Early Check-out</h3>
            <p>You're checking out before 8 hours. Please provide a reason:</p>
            <p className="worked-hours">
              Worked: {calculateWorkedHours().toFixed(1)} hours
            </p>
            <textarea
              value={earlyClockOutReason}
              onChange={(e) => setEarlyClockOutReason(e.target.value)}
              placeholder="Enter reason..."
              rows="3"
            />
            <div className="modal-buttons">
              <button
                className="modal-btn cancel"
                onClick={() => {
                  setShowEarlyClockOutModal(false);
                  setEarlyClockOutReason('');
                }}
                disabled={isClockingOut}
              >
                Cancel
              </button>
              <button
                className="modal-btn submit"
                onClick={handleEarlyClockOutSubmit}
                disabled={isClockingOut}
              >
                {isClockingOut ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
